import { ifDefined } from 'lit/directives/if-defined.js';
import { ApplicationElement, html } from '../application_element/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import '../avatar/element.js';

/**
 * Component shows a truncated list of users in a single row
 *
 * @prop users - Array of users ({name, picture,...}, {name, picture...}, ...) - these users will have their avatars shown
 * @prop totalCount - Total number of users - used to calculate N in "+N users" label
 * @prop labelSuffixOn - If we truncate 90 users, default label is "+90", if this property is set to true, label becomes "+90 users" (optional)
 * @prop url - If defined, the component becomes a link to provided url (optional)
 * @prop skeleton - Loading state to prevent layout shift, if true, the component will only render a div the size of 1 avatar.
 *
 * @cssprop [--host-direction: row] Flex direction of host or `<a>` wrapper (row - [avatars, label], row-revers - [label, avatars])
 * @cssprop [--avatar-border: 4px solid var(--contrast-light)] Border for all avatars
 * @cssprop [--avatars-overlap: 16px] How much should avatars overlap?
 * @cssprop [--avatars-direction: row] The order of avatars? row - keeps passed order, row-reversed - reverses passed order.
 * @cssprop [--label-bg: transparent] Background color of label
 * @cssprop [--label-text: var(--text-primary)] Text color of label
 * @cssprop [--label-font-size: var(--font-size-base)] Font size of label
 * @cssprop [--label-font-weight: var(--body-font-weight)] Font weight of label
 * @cssprop [--label-border-radius: 0] Border radius of label
 * @cssprop [--label-padding: 0] Padding of label
 *
 */

export default class AvatarList extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static translations = translations;

  static properties = {
    users: { type: Array },
    totalCount: { type: Number },
    labelSuffixOn: { type: Boolean },
    url: { type: String },
    skeleton: { type: Boolean },
  };

  static config = {
    reverseStack: false,
  };

  constructor() {
    super();
    this.users = [];
  }

  /** @internal */
  get hasLabel() {
    return this.totalCount - this.users.length > 0;
  }

  /** @internal */
  get labelText() {
    const count = this.totalCount - this.users.length;
    return this.labelSuffixOn ? this.t('label', { count }) : `+${count}`;
  }

  renderAvatars() {
    return html`
      <div class="avatar-list">
        ${this.users.map((u, i) => this.renderAvatar(u, i))}
      </div>
    `;
  }

  renderAvatar(user, i) {
    const zIndex = this.constructor.config.reverseStack
      ? i
      : this.users.length - i;

    return html`
      <ef-avatar
        rounded
        name=${user.name}
        picture=${ifDefined(user.picture)}
        style="z-index: ${zIndex}">
      </ef-avatar>
    `;
  }

  renderLabel() {
    return html` <div class="label">${this.labelText}</div> `;
  }

  renderContent() {
    return html`
      ${this.users.length ? this.renderAvatars() : null}
      ${this.hasLabel ? this.renderLabel() : null}
    `;
  }

  renderUrlContent() {
    return html` <a href=${this.url}> ${this.renderContent()} </a> `;
  }

  render() {
    if (this.skeleton || !this.users.length)
      return html`<div class="skeleton"></div>`;

    return html`${this.url ? this.renderUrlContent() : this.renderContent()}`;
  }
}

AvatarList.registerTagName('ef-avatar-list');

customElements.get('ef-avatar-list') ||
  customElements.define('ef-avatar-list', AvatarList);
